import React, { useState, useEffect } from "react";
import {
  Card,
  CardMedia,
  Typography,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Paper,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import {
  Info as InfoIcon,
  TouchApp as TouchAppIcon,
  PersonAddAlt1 as PersonAddAlt1Icon,
  PlayArrow as PlayArrowIcon,
  AccessTime as AccessTimeIcon,
  SmartToy as SmartToyIcon,
  Person as PersonIcon,
  Games as GamesIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import VideoPlayer from "./VideoPlayer";
import "../custom css/slider.css";

// Constants
const TIME_LIMITS = {
  NO_LIMIT: -1,
  ONE_MINUTE: 1,
  THREE_MINUTES: 3,
  FIVE_MINUTES: 5,
  THIRTY_MINUTES: 30,
};

const USER_TYPES = {
  HUMAN: "Human",
  AI: "AI",
  VIEWER: "Viewer",
};

const GAME_TYPES = {
  SKILL_BUILDING: "skill_building",
  TEAM_3: "team_3",
  BLIND: "blind",
};

const SPECIAL_GAME_TYPES = [
  GAME_TYPES.SKILL_BUILDING,
  GAME_TYPES.TEAM_3,
  GAME_TYPES.BLIND,
];

// Responsive breakpoints hook
const useResponsiveBreakpoints = () => {
  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return {
    isXSmall: screenSize.width <= 320,
    isSmall: screenSize.width <= 375,
    isMedium: screenSize.width <= 414,
    isLarge: screenSize.width <= 768,
    width: screenSize.width,
    height: screenSize.height,
  };
};

// Game configuration hook
const useGameConfig = (gameType) => {
  return {
    showOpponentRadios: !SPECIAL_GAME_TYPES.includes(gameType),
    showTimeLimitRadios: !SPECIAL_GAME_TYPES.includes(gameType),
    showPlayButton: [GAME_TYPES.SKILL_BUILDING, GAME_TYPES.BLIND].includes(
      gameType
    ),
    isSpecialGame: SPECIAL_GAME_TYPES.includes(gameType),
  };
};

// User preferences hook
const useUserPreferences = (gameType) => {
  const [userType, setUserType] = useState(USER_TYPES.HUMAN);
  const [timeLimit, setTimeLimit] = useState(TIME_LIMITS.NO_LIMIT);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    try {
      const savedOpponentType = localStorage.getItem("lastOpponentHumanOrAI");
      const savedTimeLimit = localStorage.getItem("lastTimeLimit");

      if (savedOpponentType) setUserType(savedOpponentType);
      if (savedTimeLimit) setTimeLimit(Number(savedTimeLimit));
    } catch (error) {
      console.warn("Error loading saved preferences:", error);
    }
  }, []);

  useEffect(() => {
    if (gameType === GAME_TYPES.TEAM_3) {
      setTimeLimit(TIME_LIMITS.ONE_MINUTE);
      setUserType(USER_TYPES.HUMAN);
    } else if (gameType === GAME_TYPES.SKILL_BUILDING) {
      setTimeLimit(TIME_LIMITS.NO_LIMIT);
      setUserType(USER_TYPES.AI);
    }
  }, [gameType]);

  const handleOpponentChange = (event) => {
    const newValue = event.target.checked ? USER_TYPES.AI : USER_TYPES.HUMAN;
    setIsAnimating(true);
    setTimeout(() => {
      try {
        setUserType(newValue);
        localStorage.setItem("lastOpponentHumanOrAI", newValue);
      } catch (error) {
        console.warn("Error saving opponent preference:", error);
      }
      setIsAnimating(false);
    }, 200);
  };

  const handleTimeLimitChange = (event) => {
    const newValue = event.target.value;
    setIsAnimating(true);
    setTimeout(() => {
      try {
        setTimeLimit(newValue);
        localStorage.setItem("lastTimeLimit", newValue);
      } catch (error) {
        console.warn("Error saving time limit preference:", error);
      }
      setIsAnimating(false);
    }, 200);
  };

  return {
    userType,
    timeLimit,
    handleOpponentChange,
    handleTimeLimitChange,
    isAnimating,
  };
};

// Responsive Action Button Component
const ResponsiveActionButton = ({
  text,
  onClickHandler,
  icon,
  variant = "primary",
  screenSize,
  fullWidth = false,
}) => {
  const [isPressed, setIsPressed] = useState(false);

  const getVariantStyles = () => {
    switch (variant) {
      case "secondary":
        return {
          background: "linear-gradient(135deg, #ff6b6b, #ff8e8e)",
          hoverBackground: "linear-gradient(135deg, #ff5252, #ff7979)",
          color: "#ffffff",
        };
      case "success":
        return {
          background: "linear-gradient(135deg, #4caf50, #66bb6a)",
          hoverBackground: "linear-gradient(135deg, #43a047, #5cb85c)",
          color: "#ffffff",
        };
      case "gradient":
        return {
          background: "linear-gradient(135deg, #667eea, #764ba2)",
          hoverBackground: "linear-gradient(135deg, #5a6fd8, #6a42a0)",
          color: "#ffffff",
        };
      default:
        return {
          background: "linear-gradient(135deg, #4a707d, #6a9aaa)",
          hoverBackground: "linear-gradient(135deg, #5a8090, #7aaabc)",
          color: "#ffffff",
        };
    }
  };

  const variantStyles = getVariantStyles();

  // Responsive sizing
  const buttonStyles = {
    fontSize: screenSize.isXSmall
      ? "10px"
      : screenSize.isSmall
        ? "11px"
        : "12px",
    padding: screenSize.isXSmall
      ? "8px 10px"
      : screenSize.isSmall
        ? "8px 12px"
        : "10px 16px",
    minHeight: screenSize.isXSmall
      ? "36px"
      : screenSize.isSmall
        ? "40px"
        : "44px",
  };

  return (
    <Button
      onClick={onClickHandler}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      fullWidth={fullWidth}
      sx={{
        background: variantStyles.background,
        color: variantStyles.color,
        fontWeight: 600,
        fontSize: buttonStyles.fontSize,
        padding: buttonStyles.padding,
        borderRadius: "10px",
        minHeight: buttonStyles.minHeight,
        textTransform: "none",
        boxShadow: "0 3px 8px rgba(0,0,0,0.15)",
        transform: isPressed ? "scale(0.95)" : "scale(1)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          background: variantStyles.hoverBackground,
          boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
          transform: "scale(1.02)",
        },
        "&:active": {
          transform: "scale(0.95)",
        },
      }}
      startIcon={icon}
    >
      {text}
    </Button>
  );
};

// Compact Opponent Toggle Component
const OpponentToggle = ({
  userType,
  onChange,
  team,
  gameType,
  screenSize,
  t,
}) => {
  const isAI = userType === USER_TYPES.AI;

  return (
    <Box
      sx={{
        mb: 2,
        padding: screenSize.isXSmall ? "12px" : "16px",
        border: "2px solid #e8f4fd",
        borderRadius: "16px",
        backgroundColor: "linear-gradient(135deg, #f8fbff 0%, #e3f2fd 100%)",
        boxShadow: "0 4px 12px rgba(33, 150, 243, 0.08)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <GamesIcon
            sx={{
              color: "#1976d2",
              fontSize: screenSize.isXSmall ? "18px" : "20px",
            }}
          />
          <Typography
            variant="h6"
            sx={{
              color: "#1976d2",
              fontWeight: 700,
              fontSize: screenSize.isXSmall ? "13px" : "15px",
            }}
          >
            {t("Opponent")}
          </Typography>
        </Box>
        <Chip
          label={
            isAI
              ? t("AI")
              : team && gameType === GAME_TYPES.TEAM_3
                ? t("Cmd")
                : t("Human")
          }
          size="small"
          sx={{
            fontSize: screenSize.isXSmall ? "10px" : "11px",
            height: screenSize.isXSmall ? "20px" : "24px",
            fontWeight: 600,
            backgroundColor: isAI ? "#e8f5e8" : "#fff3e0",
            color: isAI ? "#2e7d32" : "#f57c00",
            border: `1px solid ${isAI ? "#81c784" : "#ffb74d"}`,
            "& .MuiChip-label": {
              fontWeight: 700,
            },
          }}
        />
      </Box>

      <FormControlLabel
        control={
          <Switch
            checked={isAI}
            onChange={onChange}
            disabled={team && gameType === GAME_TYPES.TEAM_3}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: "#4caf50",
              },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: "#4caf50",
              },
              "& .MuiSwitch-track": {
                backgroundColor: "#2196f3",
              },
            }}
          />
        }
        label={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PersonIcon
              sx={{
                fontSize: screenSize.isXSmall ? "16px" : "18px",
                color: "#1976d2",
              }}
            />
            <Typography
              sx={{
                fontSize: screenSize.isXSmall ? "12px" : "13px",
                color: "#1976d2",
                fontWeight: 600,
              }}
            >
              {team && gameType === GAME_TYPES.TEAM_3 ? t("Cmd") : t("Human")}
            </Typography>
            <Typography
              sx={{
                fontSize: screenSize.isXSmall ? "12px" : "13px",
                color: "#666",
                mx: 1,
                fontWeight: 600,
              }}
            >
              ←→
            </Typography>
            <SmartToyIcon
              sx={{
                fontSize: screenSize.isXSmall ? "16px" : "18px",
                color: "#4caf50",
              }}
            />
            <Typography
              sx={{
                fontSize: screenSize.isXSmall ? "12px" : "13px",
                color: "#4caf50",
                fontWeight: 600,
              }}
            >
              {t("AI")}
            </Typography>
          </Box>
        }
        sx={{
          margin: 0,
          width: "100%",
          justifyContent: "center",
        }}
      />
    </Box>
  );
};

// Compact Time Selector Component
const TimeSelector = ({ timeLimit, onChange, screenSize, t }) => {
  const getTimeLimitLabel = (limit) => {
    const labels = {
      [TIME_LIMITS.ONE_MINUTE]: t("MINUTES_PER_MOVE_1"),
      [TIME_LIMITS.THREE_MINUTES]: t("MINUTES_PER_GAME_3"),
      [TIME_LIMITS.FIVE_MINUTES]: t("МINUTES_PER_GAME_5"),
      [TIME_LIMITS.THIRTY_MINUTES]: t("MINUTES_PER_GAME_30"),
      [TIME_LIMITS.NO_LIMIT]: "No Limit",
    };
    return labels[limit] || "Custom";
  };

  const timeOptions = [
    {
      value: TIME_LIMITS.ONE_MINUTE,
      label: t("MINUTES_PER_MOVE_1"),
      icon: "⚡", // Lightning for quick/fast
    },
    {
      value: TIME_LIMITS.THREE_MINUTES,
      label: t("MINUTES_PER_GAME_3"),
      icon: "🏃", // Running for medium speed
    },
    {
      value: TIME_LIMITS.FIVE_MINUTES,
      label: t("МINUTES_PER_GAME_5"),
      icon: "🚶", // Walking for relaxed pace
    },
    {
      value: TIME_LIMITS.THIRTY_MINUTES,
      label: t("MINUTES_PER_GAME_30"),
      icon: "🧘", // Meditation for thoughtful/slow
    },
  ];

  return (
    <Box
      sx={{
        mb: 2,
        padding: screenSize.isXSmall ? "12px" : "16px",
        border: "2px solid #fdf2e9",
        borderRadius: "16px",
        backgroundColor: "linear-gradient(135deg, #fffbf5 0%, #fdf2e9 100%)",
        boxShadow: "0 4px 12px rgba(255, 152, 0, 0.08)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AccessTimeIcon
            sx={{
              color: "#f57c00",
              fontSize: screenSize.isXSmall ? "18px" : "20px",
            }}
          />
          <Typography
            variant="h6"
            sx={{
              color: "#f57c00",
              fontWeight: 700,
              fontSize: screenSize.isXSmall ? "13px" : "15px",
            }}
          >
            {t("Time")}
          </Typography>
        </Box>
        <Chip
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography sx={{ fontSize: "inherit" }}>
                {timeOptions.find((opt) => opt.value === timeLimit)?.icon ||
                  "⏰"}
              </Typography>
              <Typography sx={{ fontSize: "inherit" }}>
                {getTimeLimitLabel(timeLimit)}
              </Typography>
            </Box>
          }
          size="small"
          sx={{
            fontSize: screenSize.isXSmall ? "10px" : "11px",
            height: screenSize.isXSmall ? "20px" : "24px",
            fontWeight: 700,
            backgroundColor: "#e1f5fe",
            color: "#0277bd",
            border: "1px solid #81d4fa",
            "& .MuiChip-label": {
              fontWeight: 700,
            },
          }}
        />
      </Box>

      <FormControl
        fullWidth
        size="small"
        sx={{
          margintTop: "10px",
          "& .MuiOutlinedInput-root": {
            fontSize: screenSize.isXSmall ? "12px" : "13px",
            borderRadius: "12px",
            backgroundColor: "#ffffff",
            border: "2px solid #ffcc80",
            "&:hover": {
              border: "2px solid #ffb74d",
            },
            "&.Mui-focused": {
              border: "2px solid #ff9800",
              boxShadow: "0 0 0 3px rgba(255, 152, 0, 0.1)",
            },
          },
          "& .MuiInputLabel-root": {
            fontSize: screenSize.isXSmall ? "12px" : "13px",
            color: "#f57c00",
            fontWeight: 600,
            "&.Mui-focused": {
              color: "#f57c00",
            },
          },
          "& .MuiSelect-select": {
            color: "#e65100",
            fontWeight: 600,
          },
        }}
      >
        {/* <InputLabel>{t("Select Time Limit")}</InputLabel> */}
        <Select
          value={timeLimit}
          onChange={onChange}
          label={t("Select Time Limit")}
          IconComponent={ExpandMoreIcon}
        >
          {timeOptions.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              sx={{
                fontSize: screenSize.isXSmall ? "12px" : "13px",
                padding: screenSize.isXSmall ? "8px 12px" : "10px 16px",
                color: "#e65100",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 1,
                "&:hover": {
                  backgroundColor: "#fff3e0",
                },
                "&.Mui-selected": {
                  backgroundColor: "#ffe0b2",
                  color: "#bf360c",
                  fontWeight: 700,
                  "&:hover": {
                    backgroundColor: "#ffcc80",
                  },
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  width: "100%",
                }}
              >
                <Typography
                  sx={{
                    fontSize: screenSize.isXSmall ? "14px" : "16px",
                    lineHeight: 1,
                  }}
                >
                  {option.icon}
                </Typography>
                <Typography
                  sx={{
                    fontSize: screenSize.isXSmall ? "12px" : "13px",
                    fontWeight: "inherit",
                  }}
                >
                  {option.label}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

const GameOpponentSelectionItem = ({
  onOpponentSelected,
  onFriendSelected,
  game_type_id,
  name,
  description,
  imagePath,
  videoPath,
  autoPlay,
  gameType,
  isSelected,
  onSelect,
  team,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const screenSize = useResponsiveBreakpoints();

  // UI state
  const [infoOpen, setInfoOpen] = useState(false);
  const [cardHovered, setCardHovered] = useState(false);

  // Custom hooks
  const gameConfig = useGameConfig(gameType);
  const { userType, timeLimit, handleOpponentChange, handleTimeLimitChange } =
    useUserPreferences(gameType);

  // Configuration
  const isMainCard = game_type_id === "0";
  const showChallengeFriend =
    (gameConfig.showOpponentRadios && userType !== USER_TYPES.AI && !team) ||
    gameType === GAME_TYPES.TEAM_3;

  // Event handlers
  const handleSelectOpponentClick = () => {
    onOpponentSelected();
    if (gameType === GAME_TYPES.SKILL_BUILDING) navigate("/learn");
    if (gameType === GAME_TYPES.BLIND) navigate("/blind");
  };

  const handleInfoClick = (e) => {
    e.stopPropagation();
    setInfoOpen(true);
  };

  // Responsive card dimensions
  const getCardDimensions = () => {
    const maxWidth = Math.min(screenSize.width - 32, 600); // 16px margin on each side
    const width = screenSize.isXSmall
      ? maxWidth
      : screenSize.isSmall
        ? Math.min(maxWidth, 340)
        : screenSize.isMedium
          ? Math.min(maxWidth, 380)
          : screenSize.isLarge
            ? Math.min(maxWidth, 450)
            : 500;

    const height = screenSize.isXSmall
      ? 660
      : screenSize.isSmall
        ? 630
        : screenSize.isMedium
          ? 620
          : screenSize.isLarge
            ? 650
            : 720;

    return { width, height };
  };

  const cardDimensions = getCardDimensions();

  // Responsive video height
  const videoHeight = screenSize.isXSmall
    ? "140px"
    : screenSize.isSmall
      ? "160px"
      : screenSize.isMedium
        ? "180px"
        : screenSize.isLarge
          ? "220px"
          : "260px";

  return (
    <Card
      onClick={onSelect}
      onMouseEnter={() => setCardHovered(true)}
      onMouseLeave={() => setCardHovered(false)}
      sx={{
        width: cardDimensions.width,
        height: cardDimensions.height,
        borderRadius: "16px",
        position: "relative",
        cursor: "pointer",
        overflow: "hidden",
        border: `3px solid ${cardHovered ? "#3498db" : "rgba(52, 152, 219, 0.2)"}`,
        boxShadow: cardHovered
          ? "0 16px 40px rgba(52, 152, 219, 0.2)"
          : "0 8px 24px rgba(52, 152, 219, 0.08)",
        backgroundColor: "linear-gradient(145deg, #ffffff 0%, #f8fbff 100%)",
        textAlign: "center",
        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        transform: cardHovered ? "translateY(-4px)" : "translateY(0)",
        margin: "0 auto",
        "&:active": {
          transform: "translateY(0) scale(0.98)",
        },
      }}
    >
      {/* Video Section */}
      <CardMedia
        component="div"
        sx={{
          width: "100%",
          height: videoHeight,
          position: "relative",
          overflow: "hidden",
          borderRadius: "16px 16px 0 0",
        }}
      >
        <VideoPlayer
          isSelected={isSelected}
          videoSrc={videoPath}
          autoPlay={autoPlay}
          loop={false}
          muted
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />

        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "40%",
            background: "linear-gradient(transparent, rgba(0,0,0,0.3))",
            pointerEvents: "none",
          }}
        />
      </CardMedia>

      {/* Content Section */}
      {!isMainCard && (
        <Box
          sx={{
            padding: screenSize.isXSmall ? "12px" : "16px",
            height: `calc(100% - ${videoHeight})`,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <Box>
            {/* Title with Info Button */}
            <Box
              sx={{
                cursor: "pointer",
                padding: "12px",
                borderRadius: "15px",
                border: "2px solid #4a707d",
                background: "linear-gradient(135deg, #4a707d, #6a9aaa)",
                color: "white",
                boxShadow: "0 4px 12px rgba(74, 112, 125, 0.3)",
                marginBottom: "16px",
                transition: "all 0.3s ease",
                position: "relative",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 16px rgba(74, 112, 125, 0.4)",
                  background: "linear-gradient(135deg, #5a8090, #7aaabc)",
                },
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  textAlign: "center",
                  fontSize: {
                    xs: "13px",
                    sm: "16px",
                    md: "25px",
                    lg: "25px",
                  },
                  textShadow: "0 2px 4px rgba(0,0,0,0.3)",
                  marginBottom: "8px",
                }}
              >
                {name}
              </Typography>
              <IconButton
                onClick={handleInfoClick}
                size="small"
                sx={{
                  position: "absolute",
                  top: "50%",
                  right: "12px",
                  transform: "translateY(-50%)",
                  color: "white",
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  backdropFilter: "blur(10px)",
                  border: "2px solid rgba(255,255,255,0.3)",
                  width: "36px",
                  height: "36px",
                  zIndex: 10,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.3)",
                    transform: "translateY(-50%) scale(1.1)",
                    boxShadow: "0 4px 12px rgba(255, 255, 255, 0.4)",
                  },
                }}
              >
                <InfoIcon
                  sx={{ fontSize: screenSize.isXSmall ? "16px" : "18px" }}
                />
              </IconButton>
            </Box>

            {/* Game Image for special games */}
            {!gameConfig.showOpponentRadios && (
              <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                <Paper
                  elevation={6}
                  sx={{
                    borderRadius: "10px",
                    overflow: "hidden",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.02)",
                      boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
                    },
                  }}
                >
                  <img
                    src={imagePath}
                    alt={name}
                    style={{
                      width: screenSize.isXSmall
                        ? "250px"
                        : screenSize.isSmall
                          ? "250px"
                          : screenSize.isMedium
                            ? "250px"
                            : "280px",
                      height: screenSize.isXSmall
                        ? "250px"
                        : screenSize.isSmall
                          ? "250px"
                          : screenSize.isMedium
                            ? "250px"
                            : "280px",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </Paper>
              </Box>
            )}

            {/* Opponent Selection */}
            {gameConfig.showOpponentRadios && (
              <OpponentToggle
                userType={userType}
                onChange={handleOpponentChange}
                team={team}
                gameType={gameType}
                screenSize={screenSize}
                t={t}
              />
            )}

            {/* Time Limit Selection */}
            {gameConfig.showTimeLimitRadios && (
              <TimeSelector
                timeLimit={timeLimit}
                onChange={handleTimeLimitChange}
                screenSize={screenSize}
                t={t}
              />
            )}
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              flexDirection: screenSize.isXSmall ? "column" : "row",
              gap: 1,
              justifyContent: "center",
              mt: 2,
              marginTop: "5px",
            }}
          >
            {/* Select Game Button */}
            {(gameConfig.showOpponentRadios) && (
              <ResponsiveActionButton
                text={t("Pick This Game")}
                onClickHandler={handleSelectOpponentClick}
                icon={<TouchAppIcon />}
                variant="gradient"
                screenSize={screenSize}
                fullWidth={screenSize.isXSmall}
              />
            )}

            {/* Play Button */}
            {gameConfig.showPlayButton && (
              <ResponsiveActionButton
                text={t("Play")}
                onClickHandler={handleSelectOpponentClick}
                icon={<PlayArrowIcon />}
                variant="success"
                screenSize={screenSize}
                fullWidth={screenSize.isXSmall}
              />
            )}

            {/* Challenge Friends Button */}
            {showChallengeFriend && (
              <ResponsiveActionButton
                text={
                  screenSize.isXSmall ? t("Challenge") : t("Challenge Friends")
                }
                onClickHandler={onFriendSelected}
                icon={<PersonAddAlt1Icon />}
                variant="secondary"
                screenSize={screenSize}
                fullWidth={screenSize.isXSmall}
              />
            )}
          </Box>
        </Box>
      )}

      {/* Main Card Content */}
      {isMainCard && (
        <Box
          sx={{
            position: "relative",
            height: "100%",
            backgroundColor: "black",
          }}
        >
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: screenSize.isXSmall
                ? "16px"
                : screenSize.isSmall
                  ? "18px"
                  : screenSize.isMedium
                    ? "20px"
                    : "24px",
              textAlign: "center",
              color: "#ffffff",
              textShadow: "0 4px 8px rgba(0,0,0,0.8)",
              letterSpacing: "1px",
              textTransform: "uppercase",
              p: 2,
              pt: 3,
            }}
          >
            {t("Select Your Game Type")}
          </Typography>

          <Box
            sx={{
              position: "absolute",
              top: screenSize.isXSmall ? "80px" : "90px",
              left: "50%",
              transform: "translateX(-50%)",
              width: screenSize.isXSmall
                ? "200px"
                : screenSize.isSmall
                  ? "240px"
                  : screenSize.isMedium
                    ? "280px"
                    : "320px",
              height: screenSize.isXSmall
                ? "200px"
                : screenSize.isSmall
                  ? "240px"
                  : screenSize.isMedium
                    ? "280px"
                    : "320px",
              backgroundImage:
                'url("https://res.cloudinary.com/dnlgtek2a/image/upload/v1743426566/Pictures/logo.jpg")',
              backgroundSize: "cover",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              opacity: 0.9,
              zIndex: 10,
              borderRadius: "12px",
              boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateX(-50%) scale(1.05)",
                opacity: 1,
              },
            }}
          />
        </Box>
      )}

      {/* Enhanced Dialog */}
      <Dialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            background:
              "linear-gradient(145deg, rgba(255,255,255,0.95), rgba(255,255,255,0.9))",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(255,255,255,0.2)",
            boxShadow: "0 16px 48px rgba(0,0,0,0.3)",
            margin: screenSize.isXSmall ? "8px" : "16px",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #4a707d, #6a9aaa)",
            color: "#ffffff",
            fontWeight: 700,
            fontSize: screenSize.isXSmall ? "14px" : "16px",
            borderRadius: "16px 16px 0 0",
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            py: 2,
          }}
        >
          <InfoIcon />
          {t(name)}
        </DialogTitle>
        <DialogContent
          sx={{
            padding: screenSize.isXSmall ? "12px" : "20px",
            fontSize: screenSize.isXSmall ? "13px" : "15px",
          }}
        >
          <Typography
            sx={{
              lineHeight: 1.6,
              color: "#333",
              fontSize: screenSize.isXSmall ? "13px" : "15px",
            }}
          >
            {description}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            padding: screenSize.isXSmall ? "8px 12px" : "12px 20px",
            justifyContent: "center",
          }}
        >
          <Button
            onClick={(e) => {
              e.stopPropagation();
              setInfoOpen(false);
            }}
            sx={{
              background: "linear-gradient(135deg, #4a707d, #6a9aaa)",
              color: "white",
              fontWeight: 600,
              padding: "8px 24px",
              borderRadius: "12px",
              fontSize: screenSize.isXSmall ? "12px" : "14px",
              "&:hover": {
                background: "linear-gradient(135deg, #5a8090, #7aaabc)",
              },
            }}
          >
            {t("Close")}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default GameOpponentSelectionItem;
